import { useEffect, useState } from "react";
import { CSSObject } from "@emotion/react";
import { IonToggle } from "@ionic/react";
import { subPhotoPlaceholderPNG } from "swing-assets";
import { ChoiceButton } from "swing-components";

import {
  ModalSubFeedbackBody,
  ModalSubFeedbackFooter,
  ModalSubFeedbackHeader,
  type FeedbackRating,
  type PositiveStep,
  type Step,
  type SubFeedbackRecipient,
} from "~components/ModalSubFeedback";
import { SwingHeroImageWrapper } from "~components/Organisms";
import { getFeatureFlags, translate } from "~utils";

/********* Styles ********/
const styles: Record<string, CSSObject> = {
  bodyContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  profileWrapper: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  questionText: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 16,
  },
  choiceButtonsContainer: {
    display: "flex",
    gap: 16,
  },
  noteText: {
    lineHeight: "24px",
    margin: "16px 0 0",
    fontSize: 14,
  },
  toggleContainer: {
    display: "flex",
    padding: "16px 0",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

/********* Types ********/
type ModalSubFeedbackContentRatingProps = {
  sub?: SubFeedbackRecipient;
  feedbackRating?: FeedbackRating;
  isFavoritedSub?: boolean;
  onDismiss: () => void;
  setFeedbackRating: (rating: FeedbackRating) => void;
  setIsFavoritedSub: (isFavSub: boolean) => void;
  setStep: (step: Step) => void;
  originalIsFavoritedSub?: boolean;
  setPositiveStep: (step: PositiveStep) => void;
  submitPositiveRatingOnly: () => void;
  hasSubmissionError?: boolean;
};

/********* Component ********/
export function ModalSubFeedbackContentRating(props: ModalSubFeedbackContentRatingProps) {
  const {
    feedbackRating,
    isFavoritedSub,
    onDismiss,
    setFeedbackRating,
    setIsFavoritedSub,
    setStep,
    sub,
    originalIsFavoritedSub,
    submitPositiveRatingOnly,
    hasSubmissionError,
    setPositiveStep,
  } = props;

  const [isToggleChecked, setIsToggleChecked] = useState(isFavoritedSub);

  useEffect(() => {
    if (hasSubmissionError === false && feedbackRating === "RATING_POSITIVE") {
      setPositiveStep("summary");
      setStep(feedbackRating);
    }
  }, [hasSubmissionError, feedbackRating, setPositiveStep, setStep]);

  /********* Constants ********/
  const isPositiveOrNeutral =
    feedbackRating === "RATING_POSITIVE" || feedbackRating === "RATING_NEUTRAL";

  /********* Render ********/
  return (
    <>
      <ModalSubFeedbackHeader title={translate("GIVE_FEEDBACK_LABEL")} onDismiss={onDismiss} />
      <ModalSubFeedbackBody hasApiError={hasSubmissionError}>
        <div css={styles.bodyContainer}>
          <div css={styles.profileWrapper}>
            {sub?.isSwingHero ? (
              <div css={{ flexShrink: 0 }}>
                <SwingHeroImageWrapper>
                  <ProfileImage name={sub?.name} imgUrl={sub?.profileImgUrl} />
                </SwingHeroImageWrapper>
              </div>
            ) : (
              <ProfileImage name={sub?.name} imgUrl={sub?.profileImgUrl} />
            )}
            <p>{translate("MSF_FEEDBACK_CONTENT_RATING_MESSAGE", sub?.name)}</p>
          </div>
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p css={styles.questionText}>
              {translate("MSF_FEEDBACK_QUESTION_INVITE_BACK", sub?.name)}
            </p>
            <div css={styles.choiceButtonsContainer}>
              <ChoiceButton
                icon={{ name: "Face Positive", color: "green500" }}
                label={translate("MSF_CHOICE_BUTTON_POSITIVE_LABEL")}
                value="RATING_POSITIVE"
                isSelected={feedbackRating === "RATING_POSITIVE"}
                onChange={(rating: string) => setFeedbackRating(rating as FeedbackRating)}
                activeBgColor="green200"
              />
              <ChoiceButton
                icon={{ name: "Face Neutral", color: "yellow600" }}
                label={translate("MSF_CHOICE_BUTTON_NEUTRAL_LABEL")}
                value="RATING_NEUTRAL"
                isSelected={feedbackRating === "RATING_NEUTRAL"}
                onChange={(rating: string) => setFeedbackRating(rating as FeedbackRating)}
                activeBgColor="yellow200"
              />
              <ChoiceButton
                icon={{ name: "Face Negative", color: "red500" }}
                label={translate("MSF_CHOICE_BUTTON_NEGATIVE_LABEL")}
                value="RATING_NEGATIVE"
                isSelected={feedbackRating === "RATING_NEGATIVE"}
                onChange={(rating: string) => setFeedbackRating(rating as FeedbackRating)}
                activeBgColor="red200"
              />
            </div>
            <p css={styles.noteText}>
              {translate("MSF_FEEDBACK_PUBLIC_SHARING_NOTE")}{" "}
              {originalIsFavoritedSub && translate("MSF_ADD_SUB_TO_FAVORITES_ALREADY_IN_FAVORITES")}
            </p>
          </div>
          {isPositiveOrNeutral && feedbackRating && !originalIsFavoritedSub && (
            <div css={styles.toggleContainer}>
              <p css={{ fontWeight: 600, fontSize: 16 }}>
                {translate("MSF_ADD_SUB_TO_FAVORITES", sub?.name)}
              </p>
              <IonToggle
                aria-label="Add to School Favorites toggle"
                checked={isToggleChecked}
                onIonChange={(e) => {
                  setIsToggleChecked(e.detail.checked);
                  setIsFavoritedSub(e.detail.checked);
                }}
              />
            </div>
          )}
        </div>
      </ModalSubFeedbackBody>
      <ModalSubFeedbackFooter
        buttonPrimary={{
          onClick: () =>
            getFeatureFlags().SUB_RATINGS_V3_P1 && feedbackRating === "RATING_POSITIVE"
              ? submitPositiveRatingOnly()
              : feedbackRating && setStep(feedbackRating),
          isDisabled: !feedbackRating,
          text:
            getFeatureFlags().SUB_RATINGS_V3_P1 && feedbackRating === "RATING_POSITIVE"
              ? translate("LABEL_SUBMIT")
              : translate("LABEL_NEXT"),
        }}
        // Show a secondary "Next" button only for positive feedback.
        // In this case, the primary button is repurposed to
        // submit feedback immediately, so the secondary button
        // is needed to continue to the next step if desired.
        {...(getFeatureFlags().SUB_RATINGS_V3_P1 && feedbackRating === "RATING_POSITIVE"
          ? {
              buttonSecondary: {
                onClick: () => feedbackRating && setStep(feedbackRating),
                isDisabled: !feedbackRating,
                text:
                  feedbackRating === "RATING_POSITIVE"
                    ? translate("MSF_ADD_DETAILS_CTA")
                    : translate("LABEL_NEXT"),
                hideIcon: true,
              },
            }
          : {})}
      />
    </>
  );
}

/********* Helper Components ********/
function ProfileImage(props: { imgUrl?: string; name?: string }) {
  const { imgUrl, name } = props;
  return (
    <img
      css={{ width: 92, height: 92, borderRadius: "50%", flexShrink: 0 }}
      src={imgUrl || subPhotoPlaceholderPNG}
      alt={name}
    />
  );
}
